import type { DynamicColumnInfo } from "@eduviaos/shared";

/**
 * Phase H6c — pure helpers for the dynamic-column section of the
 * student importer UI. Extracted from ``FirstRunImportStudentsPage``
 * so the page module stays component-only (the React Fast Refresh
 * eslint rule rejects mixing component + non-component exports), and
 * so the helpers are trivially unit-testable from any future Vitest
 * harness without dragging the page's React tree along.
 */

/**
 * Build a key → label lookup from the active student-record form's
 * ``dynamic_columns`` response slice. The map is consumed by
 * ``InvalidRowDisplay`` so admins see the institute-authored field
 * label (e.g. ``Blood Group``) instead of the raw snake_case key
 * (e.g. ``blood_group``) for dynamic-column rejections. Canonical
 * columns (``date_of_birth``, ``email``, …) are not in the map and
 * fall back to the raw key, preserving the C1c contract.
 *
 * Returns an empty object when no form is configured (defensive — the
 * caller already short-circuits in that case, but this keeps the
 * helper safe to call unconditionally).
 */
export function buildDynamicFieldLabelMap(
  dynamicColumns: readonly DynamicColumnInfo[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const col of dynamicColumns) {
    map[col.key] = col.label;
  }
  return map;
}

/**
 * Compose a single-line ``title`` tooltip describing a dynamic
 * column. The order — type, choices, length range, value range, help
 * text — is intentional: the most discriminating signal (what kind of
 * input is expected) comes first so a hover reveal feels useful even
 * if the admin only reads the first segment.
 *
 * Choices preview is hard-capped at four entries to keep the tooltip
 * legible for large select lists (e.g. country dropdowns); the
 * remaining count is surfaced as ``+N more``. No-op help text is
 * elided rather than rendered as an empty trailing segment.
 */
export function describeDynamicColumn(col: DynamicColumnInfo): string {
  const parts: string[] = [col.field_type];
  if (col.options.length > 0) {
    const preview = col.options.slice(0, 4).join(", ");
    parts.push(
      `choices: ${preview}${col.options.length > 4 ? `, +${col.options.length - 4} more` : ""}`,
    );
  }
  if (col.min_length != null || col.max_length != null) {
    parts.push(`length ${col.min_length ?? "*"}–${col.max_length ?? "*"}`);
  }
  if (col.min_value != null || col.max_value != null) {
    parts.push(`range ${col.min_value ?? "*"}–${col.max_value ?? "*"}`);
  }
  if (col.help_text) parts.push(col.help_text);
  return parts.join(" · ");
}
